import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Loader2, Plus, Pencil, Trash2, Globe, Wand2, Mail,
  CheckCircle, X, Clock
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import LlmRegenerateButton from "./LlmRegenerateButton";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";

const FanClubsAdmin = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ regions: {}, clubs: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [newClubDialogOpen, setNewClubDialogOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [activeTab, setActiveTab] = useState("clubs");
  const [submissions, setSubmissions] = useState({ submissions: [], counts: {} });
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  // Bulk fill — multi-select + shared template modal (Feb 2026)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState({
    region: "",
    tags: "",
    website_url: "",
    twitter_url: "",
    instagram_url: "",
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (clubs) => {
    setSelectedIds((prev) => {
      const allSelected = clubs.every((c) => prev.has(c.id));
      if (allSelected) {
        const next = new Set(prev);
        clubs.forEach((c) => next.delete(c.id));
        return next;
      }
      const next = new Set(prev);
      clubs.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const applyBulkTemplate = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Select at least one fan club first");
      return;
    }
    const payload = { ids };
    // Only include fields the admin actually filled in.
    if (bulkTemplate.region) payload.region = bulkTemplate.region;
    if (bulkTemplate.website_url !== "") payload.website_url = bulkTemplate.website_url;
    if (bulkTemplate.twitter_url !== "") payload.twitter_url = bulkTemplate.twitter_url;
    if (bulkTemplate.instagram_url !== "") payload.instagram_url = bulkTemplate.instagram_url;
    if (bulkTemplate.tags.trim()) {
      payload.tags = bulkTemplate.tags.split(",").map((t) => t.trim()).filter(Boolean);
    }
    if (Object.keys(payload).length === 1) {
      toast.error("Fill in at least one template field to apply");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await axios.patch(`${API}/fan-clubs/bulk`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      setBulkDialogOpen(false);
      setSelectedIds(new Set());
      setBulkTemplate({ region: "", tags: "", website_url: "", twitter_url: "", instagram_url: "" });
      fetchFanClubs();
    } catch (e) {
      toast.error(`Bulk apply failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBulkSaving(false);
    }
  };
  const [newClub, setNewClub] = useState({
    name: "",
    region: "uk",
    summary: "",
    instagram_url: "",
    twitter_url: "",
    website_url: ""
  });

  const regionOrder = ["uk", "north_america", "south_america", "europe", "africa", "asia", "oceania"];
  const regionIcons = {
    uk: "🇬🇧", north_america: "🌎", south_america: "🌎", 
    europe: "🇪🇺", africa: "🌍", asia: "🌏", oceania: "🌏"
  };

  useEffect(() => {
    fetchFanClubs();
  }, []);

  const fetchFanClubs = async () => {
    try {
      const response = await axios.get(`${API}/fan-clubs`, { headers: { Authorization: `Bearer ${token}` }});
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch fan clubs:", error);
      toast.error("Failed to load fan clubs");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!confirm("This will add 140+ sample fan clubs. Continue?")) return;
    try {
      const res = await axios.post(`${API}/fan-clubs/seed`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(res.data.message);
      fetchFanClubs();
    } catch (error) {
      toast.error("Failed to seed data");
    }
  };

  const handleCreateClub = async () => {
    if (!newClub.name || !newClub.summary) {
      toast.error("Please fill in name and summary");
      return;
    }
    try {
      await axios.post(`${API}/fan-clubs`, newClub, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Fan club created!");
      setNewClubDialogOpen(false);
      setNewClub({ name: "", region: "uk", summary: "", description: "", instagram_url: "", twitter_url: "", website_url: "" });
      fetchFanClubs();
    } catch (error) {
      toast.error("Failed to create fan club");
    }
  };

  const handleUpdateClub = async () => {
    if (!editingClub) return;
    try {
      await axios.put(`${API}/fan-clubs/${editingClub.id}`, {
        name: editingClub.name,
        region: editingClub.region,
        summary: editingClub.summary,
        description: editingClub.description || "",
        instagram_url: editingClub.instagram_url,
        website_url: editingClub.website_url,
        twitter_url: editingClub.twitter_url
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Fan club updated!");
      setEditDialogOpen(false);
      setEditingClub(null);
      fetchFanClubs();
    } catch (error) {
      toast.error("Failed to update fan club");
    }
  };

  const handleDeleteClub = async (clubId) => {
    if (!confirm("Delete this fan club?")) return;
    try {
      const response = await axios.delete(`${API}/fan-clubs/${clubId}`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        toast.success("Fan club deleted");
        fetchFanClubs();
      } else {
        console.error("Delete returned unexpected status:", response.status);
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Delete fan club error:", error.response?.status, error.response?.data, error.message);
      toast.error(error.response?.data?.detail || "Failed to delete fan club");
    }
  };

  const fetchSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/fan-clubs/submissions`, { headers: { Authorization: `Bearer ${token}` }});
      setSubmissions(response.data);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleApproveSubmission = async (submissionId) => {
    try {
      await axios.post(`${API}/admin/fan-clubs/submissions/${submissionId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Fan club approved and added!");
      fetchSubmissions();
      fetchFanClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve submission");
    }
  };

  const handleRejectSubmission = async (submissionId) => {
    const reason = prompt("Reason for rejection (optional):");
    try {
      await axios.post(`${API}/admin/fan-clubs/submissions/${submissionId}/reject?reason=${encodeURIComponent(reason || "")}`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Submission rejected");
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to reject submission");
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/fan-clubs/submissions/${submissionId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Submission deleted");
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete submission");
    }
  };

  // Load submissions when tab changes
  useEffect(() => {
    if (activeTab === "submissions") {
      fetchSubmissions();
    }
  }, [activeTab]);

  // Get clubs for selected region
  const displayedClubs = selectedRegion === "all" 
    ? Object.values(data.clubs).flat()
    : data.clubs[selectedRegion] || [];

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="admin-fan-clubs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Globe className="w-8 h-8 inline mr-2 text-[#2BAEE5]" />
          FAN CLUBS
        </h1>
        <div className="flex gap-2">
          {activeTab === "clubs" && data.total === 0 && (
            <Button onClick={handleSeedData} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
              <Wand2 className="w-4 h-4 mr-2" /> SEED SAMPLE DATA
            </Button>
          )}
          {activeTab === "clubs" && (
            <Button onClick={() => setNewClubDialogOpen(true)} className="bg-[#7C2C3B] hover:bg-[#63232F]">
              <Plus className="w-4 h-4 mr-2" /> ADD FAN CLUB
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("clubs")}
          className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "clubs" 
              ? "border-[#7C2C3B] text-[#7C2C3B]" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          All Clubs ({data.total})
        </button>
        <button
          onClick={() => setActiveTab("submissions")}
          className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "submissions" 
              ? "border-[#7C2C3B] text-[#7C2C3B]" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Submissions
          {submissions.counts?.pending > 0 && (
            <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
              {submissions.counts.pending}
            </span>
          )}
        </button>
      </div>

      {activeTab === "clubs" ? (
        <>
          {/* Region Stats */}
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-6">
            <button
              onClick={() => setSelectedRegion("all")}
              className={`p-3 rounded-lg text-center transition-colors ${selectedRegion === "all" ? "bg-[#7C2C3B] text-white" : "bg-white border border-slate-200 hover:border-[#7C2C3B]"}`}
            >
              <div className="text-lg font-bold">{data.total}</div>
              <div className="text-xs">All</div>
            </button>
            {regionOrder.map(region => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`p-3 rounded-lg text-center transition-colors ${selectedRegion === region ? "bg-[#7C2C3B] text-white" : "bg-white border border-slate-200 hover:border-[#7C2C3B]"}`}
              >
                <div className="text-lg font-bold">{data.clubs[region]?.length || 0}</div>
                <div className="text-xs">{regionIcons[region]}</div>
              </button>
            ))}
          </div>

          {/* Bulk apply bar — only shown when at least one club is checked. */}
          {selectedIds.size > 0 && (
            <div
              className="flex items-center justify-between bg-violet-50 border-2 border-violet-200 rounded-lg p-3 mb-4"
              data-testid="bulk-apply-bar"
            >
              <div className="text-sm text-violet-900">
                <strong>{selectedIds.size}</strong> fan club{selectedIds.size === 1 ? "" : "s"} selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-slate-600"
                  data-testid="bulk-clear-btn"
                >
                  Clear
                </Button>
                <Button
                  onClick={() => setBulkDialogOpen(true)}
                  className="bg-violet-600 hover:bg-violet-700"
                  size="sm"
                  data-testid="bulk-apply-btn"
                >
                  Apply template to selected
                </Button>
              </div>
            </div>
          )}

          {/* Clubs Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        displayedClubs.length > 0 &&
                        displayedClubs.every((c) => selectedIds.has(c.id))
                      }
                      onCheckedChange={() => toggleSelectAll(displayedClubs)}
                      aria-label="Select all on this page"
                      data-testid="bulk-select-all"
                    />
                  </TableHead>
                  <TableHead>Fan Club</TableHead>
                  <TableHead className="w-32">Region</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead className="w-28">Instagram</TableHead>
                  <TableHead className="w-28">Website</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedClubs.map(club => (
                  <TableRow key={club.id} className={selectedIds.has(club.id) ? "bg-violet-50/40" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(club.id)}
                        onCheckedChange={() => toggleSelect(club.id)}
                        aria-label={`Select ${club.name}`}
                        data-testid={`bulk-select-${club.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{club.name}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-xs">
                        {regionIcons[club.region]} {data.regions[club.region]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600 line-clamp-2">{club.summary}</p>
                    </TableCell>
                    <TableCell>
                      {club.instagram_url ? (
                        <a href={club.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs hover:opacity-80">
                          View
                        </a>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {club.website_url ? (
                        <a href={club.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-600 text-white text-xs hover:bg-slate-700">
                          View
                        </a>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <ViewLiveLink slug={club.slug} path="/fan-clubs" testId={`fanclub-${club.id}`} />
                        <Button
                          onClick={() => { setEditingClub({...club}); setEditDialogOpen(true); }}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteClub(club.id)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {displayedClubs.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Globe className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No fan clubs found. Click "Seed Sample Data" to add examples.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Submissions Tab */
        <div className="space-y-4">
          {/* Submission Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
              <div className="text-2xl font-bold text-slate-700">{submissions.counts?.total || 0}</div>
              <div className="text-xs text-slate-500">Total</div>
            </div>
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{submissions.counts?.pending || 0}</div>
              <div className="text-xs text-amber-600">Pending</div>
            </div>
            <div className="bg-green-50 rounded-lg border border-green-200 p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{submissions.counts?.approved || 0}</div>
              <div className="text-xs text-green-600">Approved</div>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{submissions.counts?.rejected || 0}</div>
              <div className="text-xs text-red-600">Rejected</div>
            </div>
          </div>

          {submissionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" />
            </div>
          ) : submissions.submissions?.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No submissions yet. Users can submit fan clubs from the public Fan Clubs page.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Fan Club</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Links</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.submissions.map(sub => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          sub.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {sub.status === 'pending' && <Clock className="w-3 h-3" />}
                          {sub.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                          {sub.status === 'rejected' && <X className="w-3 h-3" />}
                          {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{sub.name}</div>
                        <p className="text-xs text-slate-500 line-clamp-1">{sub.summary}</p>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-xs">
                          {regionIcons[sub.region]} {sub.region.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{sub.submitted_by_name}</div>
                        <div className="text-xs text-slate-400">{sub.submitted_by_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {sub.website_url && (
                            <a href={sub.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate max-w-32">
                              🌐 Website
                            </a>
                          )}
                          {sub.twitter_handle && (
                            <span className="text-xs text-slate-500">𝕏 @{sub.twitter_handle}</span>
                          )}
                          {sub.instagram_handle && (
                            <span className="text-xs text-slate-500">📷 @{sub.instagram_handle}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-500">
                          {new Date(sub.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          {sub.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => handleApproveSubmission(sub.id)}
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleRejectSubmission(sub.id)}
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Reject"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            onClick={() => handleDeleteSubmission(sub.id)}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-slate-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Fan Club</DialogTitle>
          </DialogHeader>
          {editingClub && (
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Club Name</label>
                <Input
                  value={editingClub.name}
                  onChange={(e) => setEditingClub({...editingClub, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Region</label>
                <Select value={editingClub.region} onValueChange={(val) => setEditingClub({...editingClub, region: val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regionOrder.map(region => (
                      <SelectItem key={region} value={region}>
                        {regionIcons[region]} {data.regions[region]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Summary (max 20 words)</label>
                <Textarea
                  value={editingClub.summary}
                  onChange={(e) => setEditingClub({...editingClub, summary: e.target.value})}
                  rows={3}
                  className="mt-1"
                  placeholder="Brief description of the fan club..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  {editingClub.summary?.split(/\s+/).filter(Boolean).length || 0} words
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Long description (max 200 words · LLM-editable)</label>
                  {editingClub.id && (
                    <LlmRegenerateButton
                      collection="fan_clubs"
                      docId={editingClub.id}
                      onText={(text) => setEditingClub({ ...editingClub, description: text })}
                      testId="fanclubs-regenerate-description"
                    />
                  )}
                </div>
                <Textarea
                  value={editingClub.description || ""}
                  onChange={(e) => setEditingClub({...editingClub, description: e.target.value})}
                  rows={6}
                  className="mt-1"
                  placeholder="Tell fans about this club — meeting points, history, what makes it special..."
                  data-testid="fanclubs-description-textarea"
                />
                <p className={`text-xs mt-1 ${((editingClub.description || "").split(/\s+/).filter(Boolean).length) > 200 ? "text-red-500 font-medium" : "text-slate-400"}`}>
                  {(editingClub.description || "").split(/\s+/).filter(Boolean).length} / 200 words · Your edits are never overwritten by the LLM.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Instagram URL</label>
                <Input
                  value={editingClub.instagram_url || ""}
                  onChange={(e) => setEditingClub({...editingClub, instagram_url: e.target.value})}
                  placeholder="https://instagram.com/..."
                  className="mt-1"
                  data-testid="edit-fanclub-instagram"
                />
              </div>
              <div>
                <label className="text-sm font-medium">X / Twitter URL</label>
                <Input
                  value={editingClub.twitter_url || ""}
                  onChange={(e) => setEditingClub({...editingClub, twitter_url: e.target.value})}
                  placeholder="https://x.com/..."
                  className="mt-1"
                  data-testid="edit-fanclub-twitter"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Website URL</label>
                <Input
                  value={editingClub.website_url || ""}
                  onChange={(e) => setEditingClub({...editingClub, website_url: e.target.value})}
                  placeholder="https://..."
                  className="mt-1"
                  data-testid="edit-fanclub-website"
                />
              </div>
              <Button onClick={handleUpdateClub} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
                SAVE CHANGES
              </Button>
              {/* SEO metadata editor — collapsible; auto-cleared by
                  backend when the H1/body change, so refetching after
                  save shows the "stale" banner until admin regenerates. */}
              {editingClub.id && (
                <SeoMetaEditor collection="fan_clubs" docId={editingClub.id} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Club Dialog */}
      <Dialog open={newClubDialogOpen} onOpenChange={setNewClubDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Fan Club</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium">Club Name</label>
              <Input
                value={newClub.name}
                onChange={(e) => setNewClub({...newClub, name: e.target.value})}
                placeholder="e.g. Sydney Hammers"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Region</label>
              <Select value={newClub.region} onValueChange={(val) => setNewClub({...newClub, region: val})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {regionOrder.map(region => (
                    <SelectItem key={region} value={region}>
                      {regionIcons[region]} {data.regions[region]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Summary (max 20 words)</label>
              <Textarea
                value={newClub.summary}
                onChange={(e) => setNewClub({...newClub, summary: e.target.value})}
                rows={3}
                className="mt-1"
                placeholder="Brief description of the fan club..."
              />
              <p className="text-xs text-slate-400 mt-1">
                {newClub.summary?.split(/\s+/).filter(Boolean).length || 0} words
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Long description (max 200 words)</label>
              <Textarea
                value={newClub.description || ""}
                onChange={(e) => setNewClub({...newClub, description: e.target.value})}
                rows={6}
                className="mt-1"
                placeholder="Tell fans about this club — meeting points, history, what makes it special..."
              />
              <p className={`text-xs mt-1 ${((newClub.description || "").split(/\s+/).filter(Boolean).length) > 200 ? "text-red-500 font-medium" : "text-slate-400"}`}>
                {(newClub.description || "").split(/\s+/).filter(Boolean).length} / 200 words
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Instagram URL</label>
              <Input
                value={newClub.instagram_url}
                onChange={(e) => setNewClub({...newClub, instagram_url: e.target.value})}
                placeholder="https://instagram.com/..."
                className="mt-1"
                data-testid="new-fanclub-instagram"
              />
            </div>
            <div>
              <label className="text-sm font-medium">X / Twitter URL</label>
              <Input
                value={newClub.twitter_url || ""}
                onChange={(e) => setNewClub({...newClub, twitter_url: e.target.value})}
                placeholder="https://x.com/..."
                className="mt-1"
                data-testid="new-fanclub-twitter"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Website URL</label>
              <Input
                value={newClub.website_url || ""}
                onChange={(e) => setNewClub({...newClub, website_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
                data-testid="new-fanclub-website"
              />
            </div>
            <Button onClick={handleCreateClub} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
              CREATE FAN CLUB
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Apply Template Dialog (Feb 2026) */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply template to {selectedIds.size} fan clubs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-slate-500">
              Only the fields you fill in below are written. Empty fields leave the existing values alone.
              The clubs&apos; names, descriptions and SEO metadata are never touched.
            </p>
            <div>
              <label className="text-sm font-medium">Region</label>
              <Select
                value={bulkTemplate.region || "__none__"}
                onValueChange={(v) => setBulkTemplate({ ...bulkTemplate, region: v === "__none__" ? "" : v })}
              >
                <SelectTrigger className="mt-1" data-testid="bulk-region-select">
                  <SelectValue placeholder="Leave alone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— leave alone —</SelectItem>
                  {regionOrder.map((region) => (
                    <SelectItem key={region} value={region}>
                      {regionIcons[region]} {data.regions[region]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Tags <span className="text-slate-400 font-normal">(comma-separated)</span></label>
              <Input
                value={bulkTemplate.tags}
                onChange={(e) => setBulkTemplate({ ...bulkTemplate, tags: e.target.value })}
                placeholder="e.g. official, overseas, podcast"
                className="mt-1"
                data-testid="bulk-tags"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Website URL</label>
              <Input
                value={bulkTemplate.website_url}
                onChange={(e) => setBulkTemplate({ ...bulkTemplate, website_url: e.target.value })}
                placeholder="https://... (or leave blank to skip)"
                className="mt-1"
                data-testid="bulk-website"
              />
            </div>
            <div>
              <label className="text-sm font-medium">X / Twitter URL</label>
              <Input
                value={bulkTemplate.twitter_url}
                onChange={(e) => setBulkTemplate({ ...bulkTemplate, twitter_url: e.target.value })}
                placeholder="https://x.com/... (or leave blank to skip)"
                className="mt-1"
                data-testid="bulk-twitter"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Instagram URL</label>
              <Input
                value={bulkTemplate.instagram_url}
                onChange={(e) => setBulkTemplate({ ...bulkTemplate, instagram_url: e.target.value })}
                placeholder="https://instagram.com/... (or leave blank to skip)"
                className="mt-1"
                data-testid="bulk-instagram"
              />
            </div>
            <Button
              onClick={applyBulkTemplate}
              disabled={bulkSaving}
              className="w-full bg-violet-600 hover:bg-violet-700"
              data-testid="bulk-save-btn"
            >
              {bulkSaving ? "Applying…" : `Apply to ${selectedIds.size} clubs`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FanClubsAdmin;
